import React, { createContext, useContext, useState } from 'react';

import { FilterModalTypes } from '../../../../../components/FilterModal';
import { TableHeadItemOrderBy } from '../../../../../components/TableHead';
import { FiltersToApplyProps } from '../../../../../hooks/useFilterTransformer';
import { AssetSelectedTypeMap } from '../../../../../store/product/types';
import { ProductButtonProps } from '../../DynamicButton';
import { AssetCategoriesNormalized } from '../components/AssetCategories';
import { AssetTabsTypes } from '../components/AssetTabs';
import { ProductsType } from '../constants';

export interface GlobalAssetContextReceivedData {
  isDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;
  isBatchTicket: boolean;
  accountId?: number;
  document?: string;
  accountNumber: number;
  checkingAccountNumber?: number;
  multipleAccountsIds?: number[];
  initialTabs: AssetTabsTypes.AssetTabsProps[];
  infoGetter: <T extends ProductsType>(
    productType: T,
    offer: AssetSelectedTypeMap[T],
    idSelector: keyof AssetSelectedTypeMap[T]
  ) => {
    Button: React.FC<ProductButtonProps<ProductsType>>;
    isSelected?: boolean;
    forceLoading?: boolean;
  };
}

interface GlobalAssetContextData extends Omit<GlobalAssetContextReceivedData, 'initialTabs'> {
  orderBy: TableHeadItemOrderBy | undefined;
  setOrderBy: React.Dispatch<React.SetStateAction<TableHeadItemOrderBy | undefined>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  productsTabs: AssetTabsTypes.AssetTabsProps[];
  setProductsTabs: React.Dispatch<React.SetStateAction<AssetTabsTypes.AssetTabsProps[]>>;
  selectedTab: AssetTabsTypes.AssetTabsProps;
  selectedTabIndex: number;
  availableFilters: FilterModalTypes.FilterType[];
  setAvailableFilters: React.Dispatch<React.SetStateAction<FilterModalTypes.FilterType[]>>;
  filtersToApply: FiltersToApplyProps;
  setFiltersToApply: (filters: FiltersToApplyProps) => void;
  loadingCategories: boolean;
  setLoadingCategories: React.Dispatch<React.SetStateAction<boolean>>;
  assetCategories: AssetCategoriesNormalized[];
  setAssetCategories: React.Dispatch<React.SetStateAction<AssetCategoriesNormalized[]>>;
  loadingAssets: boolean;
  setLoadingAssets: React.Dispatch<React.SetStateAction<boolean>>;
  loadingInitialAssets: boolean;
  loadingMoreAssets: boolean;
  hasSomeLoading: boolean;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  shouldRefreshAssets: boolean;
  setShouldRefreshAssets: React.Dispatch<React.SetStateAction<boolean>>;
  cacheControl: number;
  setCacheControl: React.Dispatch<React.SetStateAction<number>>;
}

interface Props extends GlobalAssetContextReceivedData {
  children: React.ReactNode;
}

const GlobalAssetContext = createContext<GlobalAssetContextData>({} as GlobalAssetContextData);

const GlobalAssetProvider: React.FC<Props> = ({ children, initialTabs, ...props }) => {
  const [cacheControl, setCacheControl] = useState(0);
  const [orderBy, setOrderBy] = useState<TableHeadItemOrderBy>();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsTabs, setProductsTabs] = useState(initialTabs);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [assetCategories, setAssetCategories] = useState<AssetCategoriesNormalized[]>([]);
  const [availableFilters, setAvailableFilters] = useState<FilterModalTypes.FilterType[]>([]);
  const [filtersToApply, setFiltersToApplyState] = useState<FiltersToApplyProps>({});
  const [shouldRefreshAssets, setShouldRefreshAssets] = useState(false);

  const selectedTab = productsTabs.find((tab) => tab.selected) || productsTabs[0];
  const selectedTabIndex = productsTabs.findIndex((tab) => tab.selected);
  const loadingInitialAssets = loadingAssets && currentPage === 1;
  const loadingMoreAssets = loadingAssets && currentPage > 1;
  const hasSomeLoading = loadingAssets || loadingCategories;

  const setFiltersToApply = (filters: FiltersToApplyProps) => {
    setCurrentPage(1);
    setFiltersToApplyState(filters);
  };

  return (
    <GlobalAssetContext.Provider
      value={{
        ...props,
        orderBy,
        setOrderBy,
        searchTerm,
        setSearchTerm,
        productsTabs,
        setProductsTabs,
        selectedTab,
        selectedTabIndex,
        availableFilters,
        setAvailableFilters,
        filtersToApply,
        setFiltersToApply,
        loadingCategories,
        setLoadingCategories,
        assetCategories,
        setAssetCategories,
        loadingAssets,
        setLoadingAssets,
        loadingInitialAssets,
        loadingMoreAssets,
        hasSomeLoading,
        currentPage,
        setCurrentPage,
        shouldRefreshAssets,
        setShouldRefreshAssets,
        cacheControl,
        setCacheControl,
      }}>
      {children}
    </GlobalAssetContext.Provider>
  );
};

const useGlobalAssetContext = (): GlobalAssetContextData => {
  const context = useContext(GlobalAssetContext);

  if (!context) {
    throw new Error('useGlobalAssetContext must be used within an GlobalAssetContext');
  }
  return context;
};

export { GlobalAssetProvider, useGlobalAssetContext, GlobalAssetContextData };
